import type { CustomAgentResultItem } from '@axilon-apps/domains-ontology-ui-state'

import { SchemaAwareResultView } from '../custom-agents/SchemaAwareResultView'
import { ActionItemCard, InsightMetaBand, RootCauseCard } from '../insights'
import type { TriageState } from '../insights'
import { ImpactsTable } from './ImpactsTable'
import { PresenterSection } from './PresenterSection'
import {
  RCA_CONSUMED_KEYS,
  leftoverRecord,
  mapActionItems,
  mapEventWindow,
  mapImpact,
  mapImpactRows,
  mapRootCauses,
  mapSummary
} from './mappers'

export interface RcaReviewBinding {
  items: CustomAgentResultItem[]
  previousById: ReadonlyMap<string, CustomAgentResultItem>
  pendingIds: ReadonlySet<string>
  readOnly: boolean
  reasonOnAgentTab: boolean
  identityOf: (item: CustomAgentResultItem) => string
  flagFor: (item: CustomAgentResultItem) => string | null
  onApprove: (item: CustomAgentResultItem) => void
  onToggleReject: (item: CustomAgentResultItem) => void
  onCompleteAction: (item: CustomAgentResultItem, done: boolean) => void
}

interface Props {
  result: Record<string, unknown>
  review?: RcaReviewBinding
}

export function RcaPresenter({ result, review }: Props) {
  const summary = mapSummary(result.one_line_summary)
  const window = mapEventWindow(result.event_window)
  const impact = mapImpact(result.impact)
  const rootCauses = mapRootCauses(result.root_causes)
  const actions = mapActionItems(result.action_items)
  const impacts = mapImpactRows(result.impacts)
  const leftover = leftoverRecord(result, RCA_CONSUMED_KEYS)
  const causeItems = review ? itemsOfType(review.items, 'root_causes') : []
  const actionItems = review ? itemsOfType(review.items, 'action_items') : []
  const binding = review
  const canReview = Boolean(binding && !binding.readOnly)

  return (
    <div className="flex flex-col gap-7">
      {summary ? <p className="m-0 text-sm leading-relaxed text-ink-2">{summary}</p> : null}

      <InsightMetaBand timeframeStart={window.start} timeframeEnd={window.end} impact={impact} />

      <PresenterSection
        label="Root causes"
        meta={rootCauses.length > 0 ? `${rootCauses.length} candidates` : undefined}
        action={
          canReview && causeItems.length > 0 ? (
            <BulkButton
              label="✕ Deny all"
              onClick={() => {
                if (!binding) return
                for (const item of causeItems) {
                  if (!binding.pendingIds.has(binding.identityOf(item))) {
                    binding.onToggleReject(item)
                  }
                }
              }}
            />
          ) : null
        }
      >
        {rootCauses.length === 0 ? (
          <p className="m-0 text-sm text-ink-3">No root causes emitted.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {rootCauses.map((cause, i) => {
              const item = causeItems[i]
              const pending = Boolean(item && binding?.pendingIds.has(binding.identityOf(item)))
              return (
                <RootCauseCard
                  key={item && binding ? binding.identityOf(item) : `${cause.title}-${i}`}
                  title={cause.title}
                  reasoning={cause.reasoning}
                  confidence={cause.confidence}
                  components={cause.components}
                  state={triageFromItem(item, pending)}
                  denialNotes={item?.feedback}
                  flag={item ? binding?.flagFor(item) : null}
                  pendingDeny={pending}
                  reasonOnAgentTab={binding?.reasonOnAgentTab}
                  onSetState={
                    canReview && item && binding
                      ? (next: TriageState) => {
                          if (next === 'accepted') binding.onApprove(item)
                          else binding.onToggleReject(item)
                        }
                      : undefined
                  }
                />
              )
            })}
          </div>
        )}
      </PresenterSection>

      <PresenterSection
        label="Action items"
        meta={actions.length > 0 ? `${actions.length} steps` : undefined}
        action={
          canReview && actionItems.length > 0 ? (
            <BulkButton
              label="✕ Reject all"
              onClick={() => {
                if (!binding) return
                for (const item of actionItems) {
                  if (!binding.pendingIds.has(binding.identityOf(item))) {
                    binding.onToggleReject(item)
                  }
                }
              }}
            />
          ) : null
        }
      >
        {actions.length === 0 ? (
          <p className="m-0 text-sm text-ink-3">No action items emitted.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {actions.map((action, i) => {
              const item = actionItems[i]
              const pending = Boolean(item && binding?.pendingIds.has(binding.identityOf(item)))
              const done = item?.work_status === 'done'
              return (
                <ActionItemCard
                  key={item && binding ? binding.identityOf(item) : `${action.title}-${i}`}
                  index={i + 1}
                  title={action.title}
                  description={action.description}
                  priority={action.priority}
                  components={action.components}
                  completed={done}
                  flag={item ? binding?.flagFor(item) : null}
                  onToggleCompleted={
                    canReview && item && binding
                      ? (next) => binding.onCompleteAction(item, next)
                      : undefined
                  }
                  onToggleReject={
                    canReview && item && binding ? () => binding.onToggleReject(item) : undefined
                  }
                  pendingReject={pending}
                />
              )
            })}
          </div>
        )}
      </PresenterSection>

      {impacts.length > 0 ? (
        <PresenterSection label="Affected processes" meta={`${impacts.length} assessed`}>
          <ImpactsTable rows={impacts} />
        </PresenterSection>
      ) : null}

      {leftover ? (
        <PresenterSection label="Other fields">
          <SchemaAwareResultView value={leftover} />
        </PresenterSection>
      ) : null}
    </div>
  )
}

function itemsOfType(items: CustomAgentResultItem[], type: string): CustomAgentResultItem[] {
  return items.filter((item) => item.type === type).sort((a, b) => a.position - b.position)
}

function triageFromItem(
  item: CustomAgentResultItem | undefined,
  pending: boolean | undefined
): TriageState {
  if (item?.approval_status === 'approved') return 'accepted'
  if (item?.approval_status === 'rejected') return 'denied'
  if (pending) return 'pending'
  return 'pending'
}

function BulkButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="cursor-pointer rounded-md border border-line bg-bg-elev px-2 py-0.5 text-11 text-ink-2 hover:text-ink"
    >
      {label}
    </button>
  )
}
